import { useEffect, useState } from "react";
import { BASE_URL } from "../apiConfig";

type Risk = {
  title: string;
  detail: string;
};

type Opportunity = {
  title: string;
  detail: string;
};

type SummaryData = {
  executive_summary: string;
  risks: Risk[];
  opportunities: Opportunity[];
  recommendations: string[];
};

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      {headerRight}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const RiskItem = ({ risk }: { risk: Risk }) => (
  <div className="rounded-xl border border-red-100 bg-red-50/60 p-4">
    <h4 className="text-sm font-semibold text-red-900">{risk.title}</h4>
    <p className="mt-1 text-sm text-red-700">{risk.detail}</p>
  </div>
);

const OpportunityItem = ({ opportunity }: { opportunity: Opportunity }) => (
  <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
    <h4 className="text-sm font-semibold text-emerald-900">{opportunity.title}</h4>
    <p className="mt-1 text-sm text-emerald-700">{opportunity.detail}</p>
  </div>
);

export default function SummaryTab() {
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSummary() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/final-results`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        setSummaryData(data);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="h-6 w-56 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (error) return <div className="p-6 md:p-8 text-red-600">Error: {error}</div>;
  if (!summaryData) return null;

  return (
    <div className="p-6 md:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Executive Summary</h1>
      </div>

      {/* Executive Summary */}
      <Card title="Overview" subtitle="High-level business summary">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <p className="text-slate-800 leading-relaxed">{summaryData.executive_summary}</p>
        </div>
      </Card>

      {/* Risks and Opportunities */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="Key Risks" subtitle="Areas that require attention">
          <div className="space-y-3">
            {summaryData.risks?.length ? (
              summaryData.risks.map((r, i) => <RiskItem key={i} risk={r} />)
            ) : (
              <p className="text-sm text-slate-500">No risks identified.</p>
            )}
          </div>
        </Card>

        <Card title="Opportunities" subtitle="Potential growth levers">
          <div className="space-y-3">
            {summaryData.opportunities?.length ? (
              summaryData.opportunities.map((o, i) => <OpportunityItem key={i} opportunity={o} />)
            ) : (
              <p className="text-sm text-slate-500">No opportunities listed.</p>
            )}
          </div>
        </Card>
      </div>

      {/* Recommendations */}
      <Card title="Recommendations" subtitle="Prioritized action items">
        {summaryData.recommendations?.length ? (
          <ul className="space-y-3">
            {summaryData.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-sm font-semibold">
                  {idx + 1}
                </span>
                <span className="text-slate-800">{rec}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No recommendations available.</p>
        )}
      </Card>
    </div>
  );
}